import { CheckCircleOutline, ErrorOutline } from "@mui/icons-material";
import {
    Box,
    CircularProgress,
    Popover,
    Stack,
    TextareaAutosize,
    Typography,
} from "@mui/material";
import { useSetNoteMutation } from "hooks/api/useLinqRequestListing";
import { useEffect, useRef, useState } from "react";
import { debounce } from "lodash";
import { useLinqRequests } from "hooks/useLinqRequests";
import useNotifications from "hooks/useNotifications";
import { useSelector } from "react-redux";

export const NoteRow = ({ defaultNote = "", id }) => {
    const {
        mutate: upsertNote,
        isLoading,
        isSuccess,
        isError,
    } = useSetNoteMutation();

    const [note, setNote] = useState(() => defaultNote);
    const [anchorEl, setAnchorEl] = useState(null);
    const { renderErrorNotification } = useNotifications();
    const { filters, resultLength, page } = useSelector(
        (state) => state.linqRequestsSlice,
    );

    const { getLinqRequests } = useLinqRequests();

    const isOpen = Boolean(anchorEl);

    const debouncedRef = useRef(
        debounce((rowId, updatedText) => {
            upsertNote({ id: rowId, note: updatedText });
        }, 600),
    );

    useEffect(() => {
        const debounced = debouncedRef.current;
        return () => {
            debounced.cancel();
        };
    }, []);

    const handleOpen = (e) => setAnchorEl(e.currentTarget);

    const handleClose = () => {
        debouncedRef.current.flush();
        setAnchorEl(null);
    };

    const handleNoteChange = (e) => {
        const updated = e.target.value;
        setNote(updated);
        debouncedRef.current(id, updated);
    };

    useEffect(() => {
        if (isSuccess) {
            getLinqRequests({
                ...filters,
                page,
                page_size: resultLength,
            });
        }

        if (isError) {
            renderErrorNotification(
                `Oops! Something went wrong updating your phone calls. Give it another shot in a minute, and if it keeps failing, our support team is here to help.`,
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isError, isSuccess]);

    if (!id) return null;

    return (
        <>
            <Stack
                justifyContent="center"
                alignItems="center"
                onClick={handleOpen}
                sx={{
                    width: "100%",
                    height: "100%",
                    cursor: "pointer",
                }}
            >
                {Boolean(note?.length) && (
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        textAlign="left"
                        sx={{ width: "100%" }}
                        noWrap
                    >
                        {note.substring(0, 40)}
                    </Typography>
                )}
            </Stack>

            <Popover
                id={id}
                open={isOpen}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: "center", horizontal: "center" }}
                transformOrigin={{ vertical: "center", horizontal: "center" }}
            >
                <Box
                    sx={{
                        background: "#DBFFD1",
                        width: 300,
                        position: "relative",
                        p: 2,
                    }}
                >
                    <TextareaAutosize
                        minRows={4}
                        maxRows={12}
                        value={note}
                        onChange={handleNoteChange}
                        style={{
                            background: "inherit",
                            width: "100%",
                            fontFamily: "inherit",
                            fontSize: "0.9rem",
                            resize: "vertical",
                            padding: "1rem",
                            paddingTop: "2.2rem",
                            borderColor: "secondary",
                            borderRadius: ".3rem",
                        }}
                    />

                    {isSuccess && (
                        <Stack
                            direction="row"
                            alignItems="center"
                            gap={0.3}
                            sx={{ position: "absolute", top: 22, right: 26 }}
                        >
                            <CheckCircleOutline
                                color="success"
                                fontSize=".8rem"
                            />
                            <Typography
                                color="success"
                                fontSize=".8rem"
                                variant="subtitle2"
                            >
                                Saved
                            </Typography>
                        </Stack>
                    )}

                    {isLoading && (
                        <Stack
                            direction="row"
                            alignItems="center"
                            gap={0.4}
                            sx={{ position: "absolute", top: 22, right: 26 }}
                        >
                            <CircularProgress color="success" size=".8rem" />
                            <Typography fontSize=".8rem" variant="subtitle2">
                                Saving...
                            </Typography>
                        </Stack>
                    )}

                    {isError && (
                        <Stack
                            direction="row"
                            alignItems="center"
                            gap={0.4}
                            sx={{ position: "absolute", top: 22, right: 26 }}
                        >
                            <ErrorOutline color="error" fontSize=".8rem" />
                            <Typography fontSize=".8rem" variant="subtitle2">
                                Changes not saved
                            </Typography>
                        </Stack>
                    )}
                </Box>
            </Popover>
        </>
    );
};

export default NoteRow;
